import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { type UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import {
  ModalsWrapperContext,
  StringIndexedObject,
  useToast,
} from '@kanda-libs/ks-design-library';
import { actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import get from 'lodash.get';
import { selectors } from 'store';

import {
  DECLINE_FINANCE_OPTION,
  DECLINE_QUOTE_MODAL_ID,
  Option,
} from './constants';

export interface DeclineQuoteModalFormValues {
  reason: string;
}

export interface DeclineQuoteModalFormHook {
  form: UseFormReturn<DeclineQuoteModalFormValues>;
  onSubmit: (
    formValues: StringIndexedObject<DeclineQuoteModalFormValues>,
  ) => void;
  isSubmitting: boolean;
  option: string | undefined;
  onOptionClick: (option: Option) => void;
  error: string | undefined;
}

export default function useDeclineQuoteModalForm(): DeclineQuoteModalFormHook {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [option, setOption] = useState<string | undefined>();

  const dispatch = useAppDispatch();
  const { id } = useSelector(selectors.getPathKey);
  const { hideModal } = useContext(ModalsWrapperContext);

  const { showError, showSuccess } = useToast();

  const form = useForm<DeclineQuoteModalFormValues>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const handleError = useCallback(
    (message: string | undefined) => {
      showError(message ?? 'Unknown error encountered');
      setIsSubmitting(false);
      hideModal(DECLINE_QUOTE_MODAL_ID);
    },
    [showError, hideModal],
  );

  const handleSuccess = useCallback(() => {
    showSuccess('Job declined and feedback submitted');
    setIsSubmitting(false);
    hideModal(DECLINE_QUOTE_MODAL_ID);
  }, [showSuccess, hideModal]);

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<DeclineQuoteModalFormValues>) => {
      if (!id) return;
      const { reason } = formValues as unknown as DeclineQuoteModalFormValues;
      if (!reason) return;
      setIsSubmitting(true);
      dispatch(
        actions.applyJob({
          params: {
            id,
          },
          body: {
            checkout_option: 'decline_job',
            declined_reason: reason,
            finance_option: DECLINE_FINANCE_OPTION,
          },
          onError: (error) => handleError(error.message),
          onSuccess: () => handleSuccess(),
        }),
      );
    },
    [id, dispatch, handleError, handleSuccess],
  );

  const onOptionClick = useCallback(
    (newOption: Option) => {
      setOption(newOption.id);
      form.setValue('reason', newOption.text);
    },
    [form],
  );

  const { errors } = form.formState;

  const error = useMemo(() => {
    if (!errors) return undefined;
    const heldError = get(errors, 'reason');
    if (!heldError) return undefined;
    if (heldError.type === 'required') {
      return 'You must provide a reason for declining the quote.';
    }
    return 'There is an issue with the form used for declining this quote';
  }, [errors]);

  return {
    form,
    onSubmit,
    isSubmitting,
    option,
    onOptionClick,
    error,
  };
}
